// Small TTL-based cache for GET API responses. Backed by an in-memory map
// (per-tab) plus localStorage (cross-tab + persistent for permanent caches).
//
// Usage:
//   const data = await cached(`/users/${uid}`, () => api.getUser(uid), { ttl: 300_000 });
//   cached.invalidate("/users/" + uid);

const MEM = new Map();           // key → { value, expires } | { pending: Promise }
const LS_PREFIX = "sm_cache:";

function lsGet(key) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj.expires && obj.expires < Date.now()) {
      localStorage.removeItem(LS_PREFIX + key);
      return null;
    }
    return obj;
  } catch { return null; }
}
function lsSet(key, value, expires) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify({ value, expires }));
  } catch { /* quota — ignore */ }
}

export async function cached(key, loader, { ttl = 5 * 60_000, persist = false } = {}) {
  // 1) hot memory cache (resolved value)
  const hit = MEM.get(key);
  if (hit && hit.expires && hit.expires > Date.now()) return hit.value;
  // 1b) in-flight loader — dedup concurrent callers so a cold load with
  // multiple components mounting in parallel doesn't fan out N identical
  // fetches. Subsequent callers await the same Promise.
  if (hit && hit.pending) return hit.pending;
  // 2) localStorage (for persist=true: barcode lookups, catalog)
  if (persist) {
    const ls = lsGet(key);
    if (ls) {
      MEM.set(key, { value: ls.value, expires: ls.expires });
      return ls.value;
    }
  }
  // 3) cold load — record the in-flight Promise BEFORE awaiting so other
  // callers in the same tick reuse it.
  const expires = ttl === Infinity ? Number.MAX_SAFE_INTEGER : Date.now() + ttl;
  const pending = (async () => {
    try {
      const value = await loader();
      MEM.set(key, { value, expires });
      if (persist) lsSet(key, value, expires);
      return value;
    } catch (e) {
      // Drop the failed in-flight entry so the next caller can retry.
      MEM.delete(key);
      throw e;
    }
  })();
  MEM.set(key, { pending });
  return pending;
}

cached.invalidate = (key) => {
  MEM.delete(key);
  try { localStorage.removeItem(LS_PREFIX + key); } catch { /* */ }
};

cached.clear = () => {
  MEM.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* */ }
};

export default cached;
